import {
  Body,
  Caption,
  Divider,
  Flex,
  Image,
  Modal,
  Row,
  SubTitle,
  Video,
  styled,
} from '@teko/ds-desktop';
import {
  IconBaseProps,
  PlayOutlined,
  RatingFilled,
  UserProfileColored,
} from '@teko/ds-icon';
import { ProductVideo, SOURCE_VIDEO } from '@teko/teko-ecommerce';
import { format } from 'date-fns';
import { t } from 'i18n';
import { StarRating } from 'modules/product-detail/shared/ProductReview/StarRating';
import React, { useState } from 'react';
import { IProductReview } from 'types';

interface IReviewItemProps {
  reviewData: IProductReview;
  showDivider?: boolean;
}

export const Star = (props?: IconBaseProps) => (
  <RatingFilled size={20} color="#fcde04" {...props} />
);

export const ReviewItem: React.FC<IReviewItemProps> = ({
  reviewData,
  showDivider,
}) => {
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  return (
    <>
      <Flex>
        <Flex direction="column">
          <Row align="middle" justify="start">
            <Avatar>
              {reviewData.userAvatar ? (
                <StyleImageAvatar
                  src={reviewData.userAvatar}
                  width={40}
                  height={40}
                />
              ) : (
                <UserProfileColored size={40} color="white" />
              )}
            </Avatar>
            <Flex direction="column" marginLeft="s8">
              <Flex direction="row" align="center" gap="s8">
                <SubTitle level={4}>
                  {reviewData.userName || t('Thành viên ẩn danh')}
                </SubTitle>
                <Caption level={3} color="#00000073">
                  {format(
                    new Date(reviewData.createdAt * 1000),
                    'dd/MM/yyyy HH:mm',
                  )}
                </Caption>
              </Flex>
              <StarRating value={reviewData.review.rating} starSize={13} />
            </Flex>
          </Row>

          <Body weight="regular" marginTop="s12">
            {reviewData.review.comment}
          </Body>
          {!!reviewData.review.images?.length ||
          !!reviewData.review.videos?.length ? (
            <Flex direction="row" gap="s4" marginTop="s4" align="center">
              {reviewData.review.videos?.map((videoUrl, index) => (
                <StyledMedia key={index}>
                  <VideoThumbnail onClick={() => setPreviewVideo(videoUrl)}>
                    <Video
                      src={videoUrl}
                      muted
                      width="100%"
                      height="60px"
                      playsInline
                      autoPlay
                      preload="metadata"
                    />

                    <PlayIconOverlay>
                      <PlayOutlined color="white" size={28} />
                    </PlayIconOverlay>
                  </VideoThumbnail>
                </StyledMedia>
              ))}
              {reviewData.review.images?.map((imageUrl, index) => (
                <StyledMedia key={index}>
                  <Image
                    src={imageUrl}
                    width="100%"
                    height="100%"
                    borderRadius="8px"
                    onClick={() => setPreviewImage(imageUrl)}
                  />
                </StyledMedia>
              ))}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
      {!!showDivider && <Divider color="divider" />}
      {previewVideo && (
        <Modal
          visible={!!previewVideo}
          onClose={() => setPreviewVideo(null)}
          width="max-content"
          showCloseIcon
          centeredBody
          footer={null}
        >
          <ProductVideo
            url={previewVideo}
            source={SOURCE_VIDEO.SYSTEM}
            videoProps={{
              width: '100%',
              controls: true,
              autoPlay: true,
              style: { maxWidth: '80vw', maxHeight: '80vh' },
            }}
          />
        </Modal>
      )}
      {previewImage && (
        <Modal
          visible={!!previewImage}
          onClose={() => setPreviewImage(null)}
          width="max-content"
          showCloseIcon
          footer={null}
        >
          <Image
            src={previewImage}
            width="100%"
            style={{ maxWidth: '80vw', maxHeight: '80vh' }}
          />
        </Modal>
      )}
    </>
  );
};

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: #00000040;
  align-items: center;
  justify-self: center;
`;

const StyledMedia = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
`;

const StyleImageAvatar = styled(Image)`
  height: 100%;
  width: 100%;
  object-fit: cover;
  display: block;
  border-radius: 50%;
`;

const VideoThumbnail = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  position: relative;
  background: #000;
  cursor: pointer;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlayIconOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;
